import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../context/Session';
import { useT } from '../i18n';
import { Empty, Icon, Loading, useConfirm, useToast } from '../components/ui';
import { MealCard, MealDetailModal } from '../components/nutrition';
import { formatDate, parseDateKey } from '../lib/dates';
import { deleteNutritionEntry, macrosOf } from '../lib/services';
import { useNutritionDays, useWaterDays } from '../lib/useUserData';
import { formatLiters } from '../lib/services';

export function NutritionRecordsList({ uid, readOnly }) {
  const t = useT();
  const toast = useToast();
  const confirm = useConfirm();
  const { nutritionDays, loading, error } = useNutritionDays(uid, { live: !readOnly });
  const { waterByDate } = useWaterDays(uid, { live: !readOnly });
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);

  if (loading) return <Loading />;
  if (error) return <Empty icon="lock">{t('errorLoadingData')}</Empty>;
  const days = nutritionDays.filter((d) => (d.entries || []).length > 0);
  if (!days.length) return <Empty icon="restaurant">{t('noRecords')}</Empty>;

  const remove = async (day, entry) => {
    const ok = await confirm({ title: t('deleteEntry'), message: t('deleteRecordingConfirm'), confirmLabel: t('deleteEntry'), danger: true });
    if (!ok) return;
    try {
      await deleteNutritionEntry(uid, parseDateKey(day.date || day.id), entry.id);
    } catch (e) {
      toast.error(t('anErrorOccurred'));
    }
  };

  return (
    <div className="gm-stack-sm">
      {days.map((d) => {
        const key = d.date || d.id;
        const tot = macrosOf(d.day_totals);
        const isOpen = open === key;
        const water = waterByDate[key];
        return (
          <div key={key} className="gm-card gm-card--flush">
            <div className="gm-list-item" style={{ cursor: 'pointer' }} onClick={() => setOpen(isOpen ? null : key)}>
              <div className="gm-icon-tile gm-icon-tile--sm"><Icon name="calendar_today" /></div>
              <div className="gm-grow">
                <div className="gm-strong">{formatDate(parseDateKey(key), t.locale, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}</div>
                <div className="gm-tiny gm-muted">
                  {d.entries.length} {t('meals').toLocaleLowerCase(t.locale)} · P {tot.protein_g}g · C {tot.carbs_g}g · F {tot.fat_g}g{water ? ` · 💧 ${formatLiters(water, t.lang)}` : ''}
                </div>
              </div>
              <div className="gm-strong">{tot.calories_kcal} kcal</div>
              <Icon name={isOpen ? 'expand_less' : 'expand_more'} />
            </div>
            {isOpen && (
              <div className="gm-stack-sm" style={{ padding: '0 14px 14px' }}>
                {d.entries.map((e) => (
                  <MealCard key={e.id} entry={e} onOpen={() => setDetail({ entry: e, key })} onDelete={readOnly ? null : () => remove(d, e)} />
                ))}
              </div>
            )}
          </div>
        );
      })}
      <MealDetailModal entry={detail?.entry} dateStr={detail ? formatDate(parseDateKey(detail.key), t.locale) : ''} onClose={() => setDetail(null)} />
    </div>
  );
}

export default function NutritionRecords() {
  const t = useT();
  const navigate = useNavigate();
  const { user } = useSession();
  return (
    <div className="gm-content gm-content--narrow gm-enter">
      <div className="gm-page-head">
        <button type="button" className="gm-icon-btn" onClick={() => navigate('/nutrition')} title={t('back')}><Icon name="arrow_back" /></button>
        <h1 className="gm-page-title">{t.upper(t('allRecords'))}</h1>
      </div>
      <NutritionRecordsList uid={user.id} />
    </div>
  );
}
